import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, ArrowRight, Truck, ScrollText, User, Sparkles, CreditCard, BookOpen,
} from "lucide-react";
import customerApi from "./customerApi";
import { hqStatus, HQ_STATUS_TONES } from "./orderStatus";

/* "At a Glance" , where the Dashboard tile lands (Aug 2 2026).
   One warm page: who you are, your credits, your recent orders + tracking. */

const AUBERGINE  = "var(--hq-deep, #5A4565)";
const PLUM       = "var(--hq-accent, #6F4A7A)";
const GOLD       = "#C9A268";
const PARCH_EDGE = "#EFE4CC";

export default function HQOverview() {
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      customerApi.get("/customer/me").then((r) => setMe(r.data)).catch(() => {}),
      customerApi.get("/customer/orders").then((r) => setOrders(r.data)).catch(() => setOrders({ console: [], historical: [] })),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="opacity-70"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>;

  const firstName = me?.name?.split(" ")[0] || "friend";
  const memberSince = String(me?.created_at || "").slice(0, 4);
  const recent = (orders?.console || []).slice(0, 5);
  const historicalCount = (orders?.historical || []).length;
  const activeCount = (orders?.console || []).filter((o) => !["completed", "cancelled", "refunded"].includes((o.stage || "").toLowerCase())).length;
  const credits = Number(me?.heritage_credits || 0);

  return (
    <div data-testid="hq-overview" className="space-y-6" style={{ color: AUBERGINE }}>
      {/* Hero , photo + name */}
      <header className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full flex-shrink-0 overflow-hidden shadow-lg"
          style={{ border: `3px solid ${GOLD}`, backgroundColor: "#FBF6E9" }}
          data-testid="hq-overview-avatar"
        >
          <img
            src={me?.profile_picture || "/photos/hq/hq-seal-purple.png"}
            alt={me?.name || "Profile"}
            className={me?.profile_picture ? "w-full h-full object-cover" : "w-full h-full object-contain p-2"}
          />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ At a Glance</div>
          <h1 className="text-4xl sm:text-5xl mt-1 leading-tight" data-testid="hq-overview-name">
            <em style={{ color: PLUM }}>{me?.name || firstName}</em>
          </h1>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {me?.customer_id && <Chip label={`Heritage ID ${me.customer_id}`} />}
            {memberSince && <Chip label={`Member since ${memberSince}`} />}
            <Chip label={`NZ$${credits.toFixed(2)} credit`} gold={credits > 0} />
          </div>
        </div>
      </header>

      {/* Stat strip */}
      <div className="grid grid-cols-3 gap-3">
        <Stat testid="hq-overview-stat-active" value={activeCount} label="Active orders" Icon={Truck} />
        <Stat testid="hq-overview-stat-total" value={(orders?.console || []).length + historicalCount} label="Orders all-time" Icon={ScrollText} />
        <Stat testid="hq-overview-stat-credit" value={`$${credits.toFixed(0)}`} label="Heritage credit" Icon={Sparkles} />
      </div>

      {/* Recent orders + tracking */}
      <section>
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[9px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>Recent Orders</div>
          <Link to="/account/orders" data-testid="hq-overview-all-orders" className="text-[10px] tracking-[0.25em] uppercase font-semibold underline" style={{ color: PLUM }}>
            View all
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="bg-white border rounded p-6 text-sm italic opacity-75" style={{ borderColor: PARCH_EDGE }} data-testid="hq-overview-no-orders">
            No current orders yet{historicalCount > 0 ? `, but we're minding ${historicalCount} of your past Heritage orders in the archive.` : "."}{" "}
            <Link to="/account/catalog" className="underline not-italic font-semibold" style={{ color: PLUM }}>Browse the catalogue</Link>
          </div>
        ) : (
          <ul className="space-y-2.5">
            {recent.map((o) => {
              const st = hqStatus(o.stage, o.payment_status);
              const tone = HQ_STATUS_TONES[st.tone] || HQ_STATUS_TONES.lilac;
              return (
                <li key={o.id}>
                  <Link
                    to={`/account/orders/${encodeURIComponent(o.id)}`}
                    data-testid={`hq-overview-order-${o.id}`}
                    className="group block bg-white border rounded p-4 hover:shadow-md transition-shadow"
                    style={{ borderColor: PARCH_EDGE }}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                          {o.order_number ? `Order #${o.order_number}` : `Ref ${String(o.id || "").slice(0, 8)}`}
                          <span className="font-normal opacity-60 text-xs ml-2">{String(o.created_at || "").slice(0, 10)}</span>
                        </div>
                        {o.tracking_number ? (
                          <div className="text-xs mt-1.5 inline-flex items-center gap-1.5" data-testid={`hq-overview-tracking-${o.id}`}>
                            <Truck className="w-3.5 h-3.5" style={{ color: PLUM }} />
                            {o.shipping_provider || "Courier"}, <strong>{o.tracking_number}</strong>
                          </div>
                        ) : (
                          <div className="text-xs mt-1.5 italic opacity-60">{st.subtitle}</div>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span
                          className="inline-block px-2.5 py-1 rounded-full text-[9px] tracking-[0.2em] uppercase font-semibold border"
                          style={{ backgroundColor: tone.bg, color: tone.ink, borderColor: tone.border }}
                        >
                          {st.label}
                        </span>
                        <div className="text-sm mt-1.5">NZ${Number(o.total || 0).toFixed(2)}</div>
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        {historicalCount > 0 && recent.length > 0 && (
          <div className="text-xs italic opacity-65 mt-2">
            Plus {historicalCount} completed order{historicalCount === 1 ? "" : "s"} in your Heritage archive ,{" "}
            <Link to="/account/orders" className="underline" style={{ color: PLUM }}>see them all</Link>.
          </div>
        )}
      </section>

      {/* Quick hops */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <QuickLink to="/account/details" Icon={User} title="My Profile" blurb="Update your details & photo" testid="hq-overview-link-profile" />
        <QuickLink to="/account/credits" Icon={CreditCard} title="Credits & Vouchers" blurb="Balances and gift vouchers" testid="hq-overview-link-credits" />
        <QuickLink to="/account/catalog" Icon={BookOpen} title="Catalogue" blurb="Browse varieties & wishlist" testid="hq-overview-link-catalog" />
      </section>
    </div>
  );
}

function Chip({ label, gold }) {
  return (
    <span
      className="inline-block px-3 py-1 rounded-full text-[9px] tracking-[0.25em] uppercase font-semibold border"
      style={gold
        ? { backgroundColor: "#FBF3DF", color: "#8A6A2F", borderColor: GOLD }
        : { backgroundColor: "var(--hq-soft, #F2EAF3)", color: PLUM, borderColor: "var(--hq-soft-edge, #E1D3E4)" }}
    >
      {label}
    </span>
  );
}

function Stat({ value, label, Icon, testid }) {
  return (
    <div className="bg-white border rounded p-4 text-center" style={{ borderColor: PARCH_EDGE }} data-testid={testid}>
      <Icon className="w-4 h-4 mx-auto mb-1.5 opacity-70" style={{ color: PLUM }} />
      <div className="text-2xl leading-none" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{value}</div>
      <div className="text-[9px] tracking-[0.25em] uppercase font-semibold opacity-60 mt-1.5">{label}</div>
    </div>
  );
}

function QuickLink({ to, Icon, title, blurb, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group bg-white border rounded p-4 hover:shadow-md transition-shadow" style={{ borderColor: PARCH_EDGE }}>
      <div className="flex items-center justify-between">
        <Icon className="w-4 h-4" style={{ color: PLUM }} />
        <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-70 transition" style={{ color: PLUM }} />
      </div>
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold mt-2" style={{ color: "#7A6A55" }}>{title}</div>
      <div className="text-xs italic opacity-70 mt-0.5">{blurb}</div>
    </Link>
  );
}
